import React, { useEffect, useState } from "react";
import { Container, Table, Button, Card, Alert } from "react-bootstrap";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import Footer from "../../Components/common/Footer";
import Header from "../../Components/common/Header";
import { baseURL, ACADEMICYEARID } from "../../API/API";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faCopy, faCheckCircle, faPhoneAlt, faMobileAlt } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "./Invoice.css";

export default function Invoice() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const axiosPrivate = useAxiosPrivate();

  const queryParams = new URLSearchParams(location.search);
  const queryName = queryParams.get("name");
  const passedPrice = location.state?.price;
  const passedDiscount = location.state?.discount;
  const passedName = queryName || location.state?.courseName || location.state?.chapterName;

  const [course, setCourse] = useState(location.state?.course || null);
  const [loading, setLoading] = useState(!course && passedPrice === undefined);
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState({
    payment_number: "01031360031",
    support_phone: "01031360031",
    instructions: "",
    invoice_discount: 0,
    invoice_total_override: "",
  });

  const coursePrice = passedPrice !== undefined ? Number(passedPrice) : course?.price || 150;
  
  // prioritizing course-specific discount
  const courseDiscount = passedDiscount !== undefined ? Number(passedDiscount) : Number(course?.discount || 0);
  const discount = courseDiscount > 0 ? courseDiscount : Number(settings.invoice_discount || 0);

  const total = settings.invoice_total_override !== undefined && settings.invoice_total_override !== "" && Number(settings.invoice_total_override) > 0
    ? Number(settings.invoice_total_override)
    : Math.max(coursePrice - discount, 0);

  useEffect(() => {
    async function fetchSettings() {
      try {
        const response = await axiosPrivate.get(`${baseURL}/learning/settings`);
        if (response.data?.data) {
          setSettings(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching settings for invoice:", error);
      }
    }
    fetchSettings();
  }, [axiosPrivate]);

  useEffect(() => {
    if (course) return;
    if (passedPrice !== undefined) {
      setLoading(false);
      return;
    }

    async function fetchCourseDetails() {
      try {
        const response = await axiosPrivate.get(`${baseURL}/learning/academics`);
        const data = response.data?.data || [];
        const found = data.find((c) => Number(c.id) === Number(courseId));
        if (found) {
          setCourse({
            ...found,
            price: found.price !== undefined && found.price !== null ? Number(found.price) : 150,
          });
        } else {
          // Fallback static course if not found in API list
          setCourse({
            id: courseId,
            name: "كورس اللغة العربية الشامل - مستر محمود الصبروط",
            price: 150,
            description: "شرح وتأسيس متكامل لكافة فروع اللغة العربية مع معادلات الهندسة النحوية والحل المكثف.",
          });
        }
      } catch (error) {
        console.error("Error fetching course details for invoice:", error);
        // Fallback
        setCourse({
          id: courseId,
          name: "كورس اللغة العربية الشامل - مستر محمود الصبروط",
          price: 150,
          description: "شرح وتأسيس متكامل لكافة فروع اللغة العربية مع معادلات الهندسة النحوية والحل المكثف.",
        });
      } finally {
        setLoading(false);
      }
    }

    fetchCourseDetails();
  }, [courseId, course, passedPrice, axiosPrivate]);

  const handleCopyNumber = () => {
    navigator.clipboard.writeText(settings.payment_number);
    setCopied(true);
    toast.success("تم نسخ الرقم بنجاح!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGoToPayment = () => {
    navigate("/payment", {
      state: {
        amount: total,
        courseName: passedName || course?.name,
        courseId: courseId,
      },
    });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري تحميل الفاتورة...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <div className="invoice-page-wrapper" dir="rtl">
      <Header />

      {/* Top Banner - Exactly like Screenshot 3 */}
      <div className="invoice-purple-banner py-5">
        <Container>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-4">
            <div className="banner-text text-end">
              <h1 className="banner-title fw-bold mb-3">{passedName || course?.name}</h1>
              <p className="banner-desc mb-2">{course?.description || "المراجعة النهائية الشاملة للمنهج الدراسي والتفوق العلمي."}</p>
              <div className="banner-support-info small">
                <span>لو بيقابلك صعوبة في الاشتراك او للاشتراك بفودافون كاش</span>
                <br />
                <span>ابعت رسالة لتيم الدعم الفني علي واتساب او التليجرام علي الرقم <strong style={{ color: "var(--brand-200-alt)" }}>{settings.support_phone}</strong> او كلمنا فون علي الرقم دا <strong style={{ color: "var(--brand-200-alt)" }}>{settings.support_phone}</strong></span>
              </div>
            </div>
            <button className="btn btn-banner-back d-flex align-items-center gap-2" onClick={() => navigate(-1)}>
              <span>العودة</span>
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          </div>
        </Container>
      </div>

      {/* Main Body - Exactly like Screenshot 1 */}
      <Container className="my-5">
        <Card className="invoice-main-card border-0 shadow-sm p-4 p-md-5">
          {/* Back to previous page link */}
          <div className="d-flex justify-content-end mb-4">
            <Link to={-1} className="back-link text-decoration-none d-flex align-items-center gap-1">
              <span>العودة للوراء</span>
              <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: "14px" }} />
            </Link>
          </div>

          <h2 className="invoice-section-title text-center fw-bold mb-5">الفاتورة</h2>

          {/* Styled Table from Screenshot 1 */}
          <div className="table-responsive mb-5">
            <Table bordered className="invoice-table text-center align-middle m-0">
              <thead>
                <tr>
                  <th>سعر الشهر</th>
                  <th>الخصم</th>
                  <th>اجمالى سعر الفاتورة النهائى</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="price-cell">{coursePrice.toFixed(2)} جنيهاً</td>
                  <td className="discount-cell">{discount.toFixed(2)} جنيهاً</td>
                  <td className="total-cell fw-bold">{total.toFixed(2)} جنيهاً</td>
                </tr>
              </tbody>
            </Table>
          </div>

          {/* Payment Details Section */}
          <div className="payment-instructions-section mt-4 p-4 rounded-3 text-end">
            <h4 className="instructions-title fw-bold mb-4">بيانات الدفع :</h4>

            <div className="payment-methods-grid mb-4">
              <div className="method-badge d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-2 me-2">
                <FontAwesomeIcon icon={faMobileAlt} className="text-primary" />
                <span>فودافون كاش (Vodafone Cash)</span>
              </div>
              <div className="method-badge d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-2">
                <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                <span>انستا باي (InstaPay)</span>
              </div>
            </div>

            <p className="payment-alert-text mb-4 text-muted">
              برجاء تحويل مبلغ وقدره <strong className="fs-5 text-dark">{total.toFixed(2)} جنيه مصري</strong> إلى الرقم الموحد أدناه:
            </p>

            {/* Copyable Phone Number Box */}
            <div className="phone-number-box d-flex align-items-center justify-content-between p-3 rounded-3 mb-4 mx-auto" style={{ maxWidth: "450px" }}>
              <span className="phone-number fw-bold fs-3 letter-spacing-1">{settings.payment_number}</span>
              <Button variant="light" className="copy-btn d-flex align-items-center gap-2 px-3 py-2" onClick={handleCopyNumber}>
                <FontAwesomeIcon icon={faCopy} />
                <span>{copied ? "تم النسخ" : "نسخ الرقم"}</span>
              </Button>
            </div>

            {/* Step-by-Step Guide */}
            <div className="steps-wrapper bg-white p-4 rounded-3 border mb-4">
              <h5 className="fw-bold mb-3">خطوات إتمام عملية الاشتراك:</h5>
              <ol className="steps-list text-end pr-3" style={{ lineHeight: "2" }}>
                {settings.instructions ? (
                  settings.instructions.split("\n").filter(line => line.trim() !== "").map((line, idx) => (
                    <li key={idx}>
                      {line.replace(/{payment_number}/g, settings.payment_number).replace(/{price}/g, `${total.toFixed(2)} جنيه`)}
                    </li>
                  ))
                ) : (
                  <>
                    <li>افتح تطبيق محفظتك الإلكترونية أو تطبيق انستا باي (InstaPay).</li>
                    <li>اختر "تحويل أموال" وأدخل الرقم الموحد: <strong className="text-primary">{settings.payment_number}</strong>.</li>
                    <li>أدخل القيمة المطلوبة للتحويل وهي: <strong className="text-primary">{total.toFixed(2)} جنيه</strong>.</li>
                    <li>أكّد عملية التحويل، والتقط لقطة شاشة (Screenshot) واضحة تؤكد نجاح التحويل.</li>
                    <li>اضغط على زر <strong style={{ color: "var(--brand-purple)" }}>"الذهاب للدفع"</strong> بالأسفل لرفع لقطة الشاشة وتأكيد عملية التحويل.</li>
                  </>
                )}
              </ol>
            </div>

            <div className="d-flex flex-column align-items-center mt-5">
              {/* Cyan Button like Screenshot 1 */}
              <Button 
                onClick={handleGoToPayment}
                className="btn-invoice-go-pay w-100 rounded-3 py-3 fw-bold mb-3 fs-5"
                style={{ maxWidth: "450px" }}
              >
                الذهاب للدفع
              </Button>
              <span className="small text-muted d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faPhoneAlt} />
                <span>للتواصل على الأرقام التالية في حال واجهت أي صعوبة: <strong className="text-dark">{settings.support_phone}</strong></span>
              </span>
            </div>
          </div>
        </Card>
      </Container>

      <Footer />
    </div>
  );
}
